import React, { useState, useRef, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Slider } from '@/components/ui/slider';
import { Switch } from '@/components/ui/switch';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import { 
  Printer, Download, Save, Eye, Palette, Type, Image, QrCode, 
  Layout, Settings, RefreshCw, Upload, Trash2
} from 'lucide-react';
import api from '@/lib/api';
import { getToken } from '@/lib/authToken';
import { BADGE_SIZES, FONT_FAMILIES, DEFAULT_DESIGN } from './badge/BadgeConstants';
import { BadgePreview } from './badge/BadgePreview';

const BadgeDesigner = ({ eventId }) => {
  const queryClient = useQueryClient();
  const previewRef = useRef(null);
  const [design, setDesign] = useState(DEFAULT_DESIGN);
  const [previewDelegate, setPreviewDelegate] = useState(null);
  const [activeTab, setActiveTab] = useState('layout');
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [bulkDownloading, setBulkDownloading] = useState(false);
  const [badgesPerPage, setBadgesPerPage] = useState('8');
  const [pageSize, setPageSize] = useState('A4');

  // Helper function to get auth token consistently
  const getToken = () => {
    try {
      const authStorage = localStorage.getItem('eventflow-auth');
      if (authStorage) {
        const parsed = JSON.parse(authStorage);
        return parsed?.state?.token || localStorage.getItem('eventflow_token');
      }
    } catch (e) {}
    return localStorage.getItem('eventflow_token');
  };

  // Fetch event branding
  const { data: event } = useQuery({
    queryKey: ['event', eventId],
    queryFn: () => api.get(`/admin/events/${eventId}`).then(r => r.data),
  });

  // Fetch badge design settings
  const { data: savedDesign } = useQuery({
    queryKey: ['badge-design', eventId],
    queryFn: () => api.get(`/admin/events/${eventId}/badge-design`).then(r => r.data),
  });

  // Seed the design from the event's branding, then layer the saved design on
  // top — so a brand-new event's badge inherits its colours and logo with no
  // manual setup, while anything the admin explicitly changed is preserved.
  const [designLoaded, setDesignLoaded] = useState(false);
  useEffect(() => {
    if (designLoaded || savedDesign === undefined || event === undefined) return;

    const brand = event?.branding || {};
    const saved = savedDesign?.design || {};
    const isUntouched = (key) => {
      const value = saved[key];
      if (!value) return true;
      return String(value).toLowerCase() === String(DEFAULT_DESIGN[key] || '').toLowerCase();
    };

    const next = { ...DEFAULT_DESIGN, ...saved };
    if (brand.primary_color && isUntouched('primaryColor')) next.primaryColor = brand.primary_color;
    if (brand.secondary_color && isUntouched('secondaryColor')) next.secondaryColor = brand.secondary_color;
    if (brand.logo_url && !saved.logoUrl) next.logoUrl = brand.logo_url;

    setDesign(next);
    setDesignLoaded(true);
  }, [savedDesign, event, designLoaded]);

  // Fetch delegates for preview
  const { data: delegates } = useQuery({
    queryKey: ['delegates', eventId],
    queryFn: () => api.get(`/admin/events/${eventId}/delegates?limit=500`).then(r => r.data),
  });

  // Save design mutation
  const saveMutation = useMutation({
    mutationFn: (designData) => api.put(`/admin/events/${eventId}/badge-design`, { design: designData }),
    onSuccess: () => {
      toast.success('Badge design saved!');
      queryClient.invalidateQueries(['badge-design', eventId]);
    },
    onError: () => toast.error('Failed to save design'),
  });

  // Set default preview delegate
  useEffect(() => {
    if (delegates?.length > 0 && !previewDelegate) {
      setPreviewDelegate(delegates[0]);
    }
  }, [delegates, previewDelegate]);

  const updateDesign = (key, value) => {
    setDesign(prev => ({ ...prev, [key]: value }));
  };

  const handleSave = () => {
    saveMutation.mutate(design);
  };

  const handleReset = () => {
    const brand = event?.branding || {};
    setDesign({
      ...DEFAULT_DESIGN,
      ...(brand.primary_color ? { primaryColor: brand.primary_color } : {}),
      ...(brand.secondary_color ? { secondaryColor: brand.secondary_color } : {}),
      ...(brand.logo_url ? { logoUrl: brand.logo_url } : {}),
    });
    toast.info('Design reset to event branding');
  };

  // Download single badge PDF
  const handleDownloadSinglePdf = async () => {
    if (!previewDelegate) {
      toast.error('Select a delegate to download badge');
      return;
    }
    
    setDownloadingPdf(true);
    try {
      const token = getToken();
      if (!token) {
        toast.error('Session expired. Please log in again.');
        return;
      }
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/events/${eventId}/delegates/${previewDelegate.id}/badge-pdf-custom`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(design)
        }
      );
      
      if (response.status === 401) {
        toast.error('Session expired. Please log in again.');
        return;
      }
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to generate PDF');
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const name = previewDelegate.name || `${previewDelegate.first_name || ''} ${previewDelegate.last_name || ''}`.trim();
      a.download = `badge_${name.replace(/\s+/g, '_')}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast.success('Badge PDF downloaded!');
    } catch (error) {
      console.error('PDF download error:', error);
      toast.error(error.message || 'Failed to download badge PDF');
    } finally {
      setDownloadingPdf(false);
    }
  };

  // Download bulk badges PDF
  const handleBulkDownload = async () => {
    setBulkDownloading(true);
    try {
      // First save the design
      await saveMutation.mutateAsync(design);
      
      const token = getToken();
      if (!token) {
        toast.error('Session expired. Please log in again.');
        return;
      }
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/events/${eventId}/badges/bulk-pdf`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            design: design,
            badges_per_page: parseInt(badgesPerPage),
            page_size: pageSize
          })
        }
      );
      
      if (response.status === 401) {
        toast.error('Session expired. Please log in again.');
        return;
      }
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to generate bulk PDF');
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `badges_${event?.name?.replace(/\s+/g, '_') || 'event'}_all.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast.success(`Bulk badges PDF downloaded! (${delegates?.length || 0} badges)`);
    } catch (error) {
      console.error('Bulk PDF download error:', error);
      toast.error(error.message || 'Failed to download bulk badges PDF');
    } finally {
      setBulkDownloading(false);
    }
  };

  const handlePrint = () => {
    const printContent = previewRef.current;
    if (!printContent) return;

    const size = BADGE_SIZES[design.size];
    const printWindow = window.open('', '', 'width=400,height=600');
    printWindow.document.write(`
      <html>
        <head>
          <title>Badge Print</title>
          <style>
            @page { size: ${size.width}mm ${size.height}mm; margin: 0; }
            body { margin: 0; padding: 0; }
          </style>
        </head>
        <body>
          ${printContent.innerHTML}
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 250);
  };

  const size = BADGE_SIZES[design.size];
  const sampleData = previewDelegate || {
    name: 'John Doe',
    company: 'Acme Corporation',
    title: 'Senior Developer',
    tier: 'VIP',
    email: 'john@example.com',
    id: 'sample123',
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Badge Designer</h2>
          <p className="text-slate-500">Customize your event badge design</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleReset}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Reset
          </Button>
          <Button onClick={handleSave} disabled={saveMutation.isPending}>
            <Save className="w-4 h-4 mr-2" />
            {saveMutation.isPending ? 'Saving...' : 'Save Design'}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Design Controls */}
        <div className="lg:col-span-2">
          <Card>
            <CardContent className="pt-6">
              <Tabs value={activeTab} onValueChange={setActiveTab}>
                <TabsList className="grid grid-cols-4 w-full">
                  <TabsTrigger value="layout" className="flex items-center gap-2">
                    <Layout className="w-4 h-4" />
                    Layout
                  </TabsTrigger>
                  <TabsTrigger value="colors" className="flex items-center gap-2">
                    <Palette className="w-4 h-4" />
                    Colors
                  </TabsTrigger>
                  <TabsTrigger value="typography" className="flex items-center gap-2">
                    <Type className="w-4 h-4" />
                    Typography
                  </TabsTrigger>
                  <TabsTrigger value="elements" className="flex items-center gap-2">
                    <Settings className="w-4 h-4" />
                    Elements
                  </TabsTrigger>
                </TabsList>

                {/* Layout Tab */}
                <TabsContent value="layout" className="space-y-6 mt-6">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Badge Size</Label>
                      <Select value={design.size} onValueChange={(v) => updateDesign('size', v)}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {Object.entries(BADGE_SIZES).map(([key, val]) => (
                            <SelectItem key={key} value={key}>{val.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Layout Alignment</Label>
                      <Select value={design.layout} onValueChange={(v) => updateDesign('layout', v)}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="centered">Centered</SelectItem>
                          <SelectItem value="left">Left Aligned</SelectItem>
                          <SelectItem value="right">Right Aligned</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Header Style</Label>
                      <Select value={design.headerStyle} onValueChange={(v) => updateDesign('headerStyle', v)}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="solid">Solid Color</SelectItem>
                          <SelectItem value="gradient">Gradient</SelectItem>
                          <SelectItem value="none">No Header</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Tier Badge Position</Label>
                      <Select value={design.tierPosition} onValueChange={(v) => updateDesign('tierPosition', v)}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="top">Top (Under Header)</SelectItem>
                          <SelectItem value="bottom">Bottom</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Border Radius: {design.borderRadius}px</Label>
                    <Slider
                      value={[design.borderRadius]}
                      onValueChange={([v]) => updateDesign('borderRadius', v)}
                      max={20}
                      step={1}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Border Width: {design.borderWidth}px</Label>
                    <Slider
                      value={[design.borderWidth]}
                      onValueChange={([v]) => updateDesign('borderWidth', v)}
                      max={5}
                      step={1}
                    />
                  </div>
                </TabsContent>

                {/* Colors Tab */}
                <TabsContent value="colors" className="space-y-6 mt-6">
                  {/* Badge Logo — uploaded here, no trip to the Branding tab */}
                  <div className="space-y-3 p-4 bg-slate-50 rounded-lg">
                    <div className="flex items-center gap-2">
                      <Image className="w-4 h-4" />
                      <Label className="font-semibold">Badge Logo</Label>
                    </div>
                    <p className="text-xs text-slate-500">
                      Printed in the badge header. Leave empty to use the event's branding logo.
                    </p>
                    <div className="space-y-2">
                      <Input
                        placeholder="Enter logo URL or upload below"
                        value={design.logoUrl || ''}
                        onChange={(e) => updateDesign('logoUrl', e.target.value)}
                        data-testid="badge-logo-url-input"
                      />
                      <div className="flex gap-2">
                        <Input
                          type="file"
                          accept="image/*"
                          className="flex-1"
                          data-testid="badge-logo-upload"
                          onChange={async (e) => {
                            const file = e.target.files?.[0];
                            if (!file) return;
                            try {
                              const formData = new FormData();
                              formData.append('file', file);
                              formData.append('file_type', 'event');
                              const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/upload`, {
                                method: 'POST',
                                headers: { Authorization: `Bearer ${getToken()}` },
                                body: formData,
                              });
                              const data = await response.json();
                              if (!data.url) throw new Error(data.detail || 'Upload failed');
                              updateDesign('logoUrl', data.url);
                              updateDesign('showLogo', true);
                              toast.success('Badge logo uploaded — remember to save the design');
                            } catch (err) {
                              toast.error(err.message || 'Failed to upload logo');
                            }
                          }}
                        />
                        {design.logoUrl && (
                          <Button
                            variant="ghost"
                            size="icon"
                            data-testid="badge-logo-clear"
                            onClick={() => updateDesign('logoUrl', '')}
                          >
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        )}
                      </div>
                      {design.logoUrl && (
                        <img
                          src={design.logoUrl.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${design.logoUrl}` : design.logoUrl}
                          alt="Badge logo preview"
                          className="h-12 w-auto rounded border bg-white p-1"
                          onError={(e) => { e.target.style.display = 'none'; }}
                        />
                      )}
                    </div>
                  </div>

                  {/* Background Image Section */}
                  <div className="space-y-3 p-4 bg-slate-50 rounded-lg">
                    <div className="flex items-center gap-2">
                      <Image className="w-4 h-4" />
                      <Label className="font-semibold">Background Image</Label>
                    </div>
                    <div className="space-y-2">
                      <div className="flex gap-2">
                        <Input
                          type="file"
                          accept="image/*"
                          className="flex-1"
                          onChange={async (e) => {
                            const file = e.target.files?.[0];
                            if (file) {
                              try {
                                const formData = new FormData();
                                formData.append('file', file);
                                formData.append('file_type', 'general');
                                const token = getToken();
                                const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/upload`, {
                                  method: 'POST',
                                  headers: { 'Authorization': `Bearer ${token}` },
                                  body: formData
                                });
                                const data = await response.json();
                                if (data.url) {
                                  const fullUrl = `${process.env.REACT_APP_BACKEND_URL}${data.url}`;
                                  updateDesign('backgroundImage', fullUrl);
                                  toast.success('Background image uploaded!');
                                }
                              } catch (err) {
                                toast.error('Failed to upload image');
                              }
                            }
                          }}
                        />
                        {design.backgroundImage && (
                          <Button 
                            variant="ghost" 
                            size="icon"
                            onClick={() => updateDesign('backgroundImage', '')}
                          >
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        )}
                      </div>
                      {design.backgroundImage && (
                        <div className="mt-2">
                          <img 
                            src={design.backgroundImage} 
                            alt="Background preview" 
                            className="h-16 w-auto rounded border"
                            onError={(e) => e.target.style.display = 'none'}
                          />
                        </div>
                      )}
                    </div>
                    {design.backgroundImage && (
                      <div className="space-y-2">
                        <Label>Background Opacity: {design.backgroundOpacity}%</Label>
                        <Slider
                          value={[design.backgroundOpacity]}
                          onValueChange={([v]) => updateDesign('backgroundOpacity', v)}
                          min={10}
                          max={100}
                          step={5}
                        />
                      </div>
                    )}
                  </div>

                  <Separator />

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Background Color</Label>
                      <div className="flex gap-2">
                        <Input
                          type="color"
                          value={design.backgroundColor}
                          onChange={(e) => updateDesign('backgroundColor', e.target.value)}
                          className="w-12 h-10 p-1 cursor-pointer"
                        />
                        <Input
                          value={design.backgroundColor}
                          onChange={(e) => updateDesign('backgroundColor', e.target.value)}
                          className="flex-1"
                        />
                      </div>
                      <p className="text-xs text-slate-500">Shows through if no background image</p>
                    </div>
                    <div className="space-y-2">
                      <Label>Primary Color</Label>
                      <div className="flex gap-2">
                        <Input
                          type="color"
                          value={design.primaryColor}
                          onChange={(e) => updateDesign('primaryColor', e.target.value)}
                          className="w-12 h-10 p-1 cursor-pointer"
                        />
                        <Input
                          value={design.primaryColor}
                          onChange={(e) => updateDesign('primaryColor', e.target.value)}
                          className="flex-1"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Secondary Color</Label>
                      <div className="flex gap-2">
                        <Input
                          type="color"
                          value={design.secondaryColor}
                          onChange={(e) => updateDesign('secondaryColor', e.target.value)}
                          className="w-12 h-10 p-1 cursor-pointer"
                        />
                        <Input
                          value={design.secondaryColor}
                          onChange={(e) => updateDesign('secondaryColor', e.target.value)}
                          className="flex-1"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Text Color</Label>
                      <div className="flex gap-2">
                        <Input
                          type="color"
                          value={design.textColor}
                          onChange={(e) => updateDesign('textColor', e.target.value)}
                          className="w-12 h-10 p-1 cursor-pointer"
                        />
                        <Input
                          value={design.textColor}
                          onChange={(e) => updateDesign('textColor', e.target.value)}
                          className="flex-1"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Border Color</Label>
                    <div className="flex gap-2">
                      <Input
                        type="color"
                        value={design.borderColor}
                        onChange={(e) => updateDesign('borderColor', e.target.value)}
                        className="w-12 h-10 p-1 cursor-pointer"
                      />
                      <Input
                        value={design.borderColor}
                        onChange={(e) => updateDesign('borderColor', e.target.value)}
                        className="flex-1"
                      />
                    </div>
                  </div>
                </TabsContent>

                {/* Typography Tab */}
                <TabsContent value="typography" className="space-y-6 mt-6">
                  <div className="space-y-2">
                    <Label>Font Family</Label>
                    <Select value={design.fontFamily} onValueChange={(v) => updateDesign('fontFamily', v)}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {FONT_FAMILIES.map(font => (
                          <SelectItem key={font.value} value={font.value}>{font.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>Name Size: {design.nameSize}pt</Label>
                    <Slider
                      value={[design.nameSize]}
                      onValueChange={([v]) => updateDesign('nameSize', v)}
                      min={12}
                      max={28}
                      step={1}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Company Size: {design.companySize}pt</Label>
                    <Slider
                      value={[design.companySize]}
                      onValueChange={([v]) => updateDesign('companySize', v)}
                      min={8}
                      max={18}
                      step={1}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Title Size: {design.titleSize}pt</Label>
                    <Slider
                      value={[design.titleSize]}
                      onValueChange={([v]) => updateDesign('titleSize', v)}
                      min={6}
                      max={14}
                      step={1}
                    />
                  </div>
                </TabsContent>

                {/* Elements Tab */}
                <TabsContent value="elements" className="space-y-6 mt-6">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                      <div className="flex items-center gap-2">
                        <Image className="w-4 h-4" />
                        <Label>Show Logo</Label>
                      </div>
                      <Switch
                        checked={design.showLogo}
                        onCheckedChange={(v) => updateDesign('showLogo', v)}
                      />
                    </div>
                    <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                      <div className="flex items-center gap-2">
                        <Type className="w-4 h-4" />
                        <Label>Show Event Name</Label>
                      </div>
                      <Switch
                        checked={design.showEventName}
                        onCheckedChange={(v) => updateDesign('showEventName', v)}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                      <div className="flex items-center gap-2">
                        <QrCode className="w-4 h-4" />
                        <Label>Show QR Code</Label>
                      </div>
                      <Switch
                        checked={design.showQrCode}
                        onCheckedChange={(v) => updateDesign('showQrCode', v)}
                      />
                    </div>
                    <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                      <Label>Show Tier Badge</Label>
                      <Switch
                        checked={design.showTier}
                        onCheckedChange={(v) => updateDesign('showTier', v)}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                      <Label>Show Company</Label>
                      <Switch
                        checked={design.showCompany}
                        onCheckedChange={(v) => updateDesign('showCompany', v)}
                      />
                    </div>
                    <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                      <Label>Show Title</Label>
                      <Switch
                        checked={design.showTitle}
                        onCheckedChange={(v) => updateDesign('showTitle', v)}
                      />
                    </div>
                  </div>

                  <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <Label>Show Badge ID</Label>
                    <Switch
                      checked={design.showBadgeId}
                      onCheckedChange={(v) => updateDesign('showBadgeId', v)}
                    />
                  </div>

                  {design.showQrCode && (
                    <div className="space-y-2">
                      <Label>QR Code Size: {design.qrSize}px</Label>
                      <Slider
                        value={[design.qrSize]}
                        onValueChange={([v]) => updateDesign('qrSize', v)}
                        min={40}
                        max={120}
                        step={5}
                      />
                    </div>
                  )}
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>
        </div>

        {/* Preview Panel */}
        <div className="space-y-4">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <Eye className="w-5 h-5" />
                Live Preview
              </CardTitle>
            </CardHeader>
            <CardContent>
              {/* Preview Delegate Selector */}
              {delegates?.length > 0 && (
                <div className="mb-4">
                  <Label className="text-sm">Preview with:</Label>
                  <Select
                    value={previewDelegate?.id || ''}
                    onValueChange={(id) => {
                      const del = delegates.find(d => d.id === id);
                      if (del) setPreviewDelegate(del);
                    }}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Select delegate" />
                    </SelectTrigger>
                    <SelectContent>
                      {delegates.map(d => (
                        <SelectItem key={d.id} value={d.id}>
                          {d.name || `${d.first_name} ${d.last_name}`}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Badge Preview */}
              <div className="flex justify-center p-4 bg-slate-100 rounded-lg">
                <BadgePreview ref={previewRef} design={design} event={event} sampleData={sampleData} />
              </div>

              {/* Action Buttons */}
              <div className="flex gap-2 mt-4">
                <Button variant="outline" className="flex-1" onClick={handlePrint}>
                  <Printer className="w-4 h-4 mr-2" />
                  Print Test
                </Button>
                <Button 
                  variant="outline" 
                  className="flex-1"
                  onClick={handleDownloadSinglePdf}
                  disabled={!previewDelegate || downloadingPdf}
                >
                  <Download className="w-4 h-4 mr-2" />
                  {downloadingPdf ? 'Generating...' : 'Download PDF'}
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Bulk Download Card */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <Download className="w-5 h-5" />
                Bulk PDF Export
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Badges per page</Label>
                <Select value={badgesPerPage} onValueChange={setBadgesPerPage}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="4">4 per page</SelectItem>
                    <SelectItem value="6">6 per page</SelectItem>
                    <SelectItem value="8">8 per page (Recommended)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>Paper size</Label>
                <Select value={pageSize} onValueChange={setPageSize}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="A4">A4</SelectItem>
                    <SelectItem value="Letter">Letter</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <Button 
                className="w-full" 
                onClick={handleBulkDownload}
                disabled={bulkDownloading}
              >
                {bulkDownloading ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Generating PDF...
                  </>
                ) : (
                  <>
                    <Download className="w-4 h-4 mr-2" />
                    Download All Badges ({delegates?.length || 0})
                  </>
                )}
              </Button>
              
              <p className="text-xs text-slate-500">
                Generates a multi-page PDF with all delegates' badges ready for printing.
              </p>
            </CardContent>
          </Card>

          {/* Quick Tips */}
          <Card>
            <CardContent className="pt-4">
              <h4 className="font-semibold mb-2">Quick Tips</h4>
              <ul className="text-sm text-slate-600 space-y-1">
                <li>• Standard badge size is 54×86mm (credit card)</li>
                <li>• Use high contrast colors for readability</li>
                <li>• QR codes should be at least 60px for scanability</li>
                <li>• Test print before bulk printing</li>
                <li>• Save your design before bulk download</li>
              </ul>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default BadgeDesigner;
